import axios from "axios";
import { isPhoneNotVerified, phoneGateAvailable, requestPhoneVerification } from "@/lib/phoneGate";
import { PHONE_ERROR_KEYS } from "@/lib/phone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Where XHR data calls go: SAME-ORIGIN `/api` in the deployed app.
 *
 * The ONE compiled bundle is published to BOTH Firebase Hosting targets — the city app
 * (`apnashahar-app-*`) and the platform admin console (`apnashahar-admin-*`) — and the admin site
 * runs a strict `connect-src 'self'` CSP (firebase.json). An ABSOLUTE data origin — the direct
 * Cloud Run service host we used to bake in via `REACT_APP_API_ORIGIN` for a ~200 ms/request win —
 * is a DIFFERENT origin from the admin host, so the browser blocked every admin XHR before it left the
 * page: the console was dead and the Google button's `/auth/google/config` probe failed, so the
 * button hid itself. A relative `/api` is same-origin on WHICHEVER host serves the page; both
 * Hosting targets rewrite `/api/**` to the Cloud Run API, so one bundle works on both.
 *
 * Widening the admin CSP to allow that Cloud Run origin is NOT the fix — same-origin is also what lets the
 * `__session` cookie (the only cookie Firebase forwards to the rewrite) reach the API at all; a
 * cross-origin call would strip it. The ~200 ms direct-Cloud-Run hop is given up deliberately (it
 * never mattered on the internal console, and the public app pays one Hosting proxy hop).
 *
 * Local dev is the one place the SPA (:3000) and the API (:8001) are genuinely separate origins
 * with no Hosting rewrite in front, so there — and only there — we keep the absolute
 * `REACT_APP_BACKEND_URL`. `resolveImage`/`ShareButton` keep `REACT_APP_BACKEND_URL` everywhere
 * (the presentable public host for media and WhatsApp share links) — see lib/media.js.
 *
 * NOTE: the built artefact must carry NO absolute Cloud Run data origin — scripts/check_web_brand.sh
 * gates the deployed output against it.
 */
export const API =
  process.env.NODE_ENV === "development" ? `${BACKEND_URL}/api` : "/api";

export const TOKEN_KEY = "biswan_token";
export const LANG_KEY = "biswan_lang";
export const TENANT_KEY = "biswan_tenant";
// The current city's tenant UUID (not its slug) — `resolveImage` (`lib/media.js`) needs it to
// rewrite a LEGACY stored `/api/media/<id>/<variant>` URL (a `cover_image_url`/`image_url`
// saved before the public media route existed) into the public form. Set alongside TENANT_KEY
// by `AppContext.loadTenant` whenever a city resolves.
export const TENANT_ID_KEY = "biswan_tenant_id";
export const CITY_TOKEN_KEY = "biswan_city_token";

export const api = axios.create({ baseURL: API });

/**
 * One list response, as an array, whatever shape the server sent.
 *
 * The API does not have one list contract. Measured against staging on 7 Sep 2026:
 *
 *     bare array   /activity/mine  /appointments/mine  /bookings/mine  /addresses
 *     {items,...}  /orders/mine  /payments/mine  /disputes/mine  /reviews/mine
 *                  /owner/khata  /owner/orders  /owner/payments
 *
 * Components called `.map` on all of them, so every object-shaped response threw
 * `TypeError: x.map is not a function`, the ErrorBoundary caught it, and the WHOLE PAGE became
 * "Something went wrong" — with the network tab showing nothing but 200s. That is how the citizen
 * Account page, My Activity, the operator console and the owner money views were all down at once.
 *
 * Worth being precise about why it hid so well: a 200 that renders a blank error page looks like a
 * backend outage and probes like a healthy service, so status-code checks clear it every time. It
 * is a SHAPE defect, and only a shape check finds it.
 *
 * This is deliberately tolerant rather than strict. Unifying the server contract is the real fix
 * and it is a breaking change for two mobile apps; until that happens the client must not crash on
 * either shape, and afterwards this stays correct for both. `null`/`undefined` (a rejected request
 * whose state never got set) collapses to `[]`, because a list that failed to load has no rows —
 * the emptiness is reported by the caller's own error state, not by pretending it errored here.
 *
 * The pattern is not new: `InventoryPanel` already did `r.data.items ?? r.data` locally. This is
 * that, in one place, so the next list does not have to remember.
 */
export function rows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

/** Store the short-lived visitor token minted by `GET /tenants/resolve`.
 *
 * Public reads are tenant-scoped by a signed token on `x-city-token`, NOT by a `tenant_id` query
 * parameter — taking the city from user input is the thing the tenancy model exists to prevent.
 * Until this was wired, neither client sent the header at all, so every signed-out read of the
 * directory, the community board and the public fulfilment routes answered 401 "Choose a city to
 * continue." Signing in hid it, because `public_db` prefers a principal's own tenant and never
 * consults the header.
 *
 * The expiry is stored as an absolute timestamp rather than the server's relative TTL, so a tab
 * left open overnight can tell the token is stale without asking.
 */
export function setCityToken(token, expiresInSeconds) {
  if (!token) return;
  const payload = {
    token,
    expiresAt: Date.now() + (Number(expiresInSeconds) || 0) * 1000,
  };
  localStorage.setItem(CITY_TOKEN_KEY, JSON.stringify(payload));
}

export function getCityToken() {
  try {
    const raw = localStorage.getItem(CITY_TOKEN_KEY);
    if (!raw) return null;
    const { token, expiresAt } = JSON.parse(raw);
    // A minute of slack: a token that expires mid-flight is a 401 the user cannot act on.
    if (!token || (expiresAt && Date.now() > expiresAt - 60_000)) return null;
    return token;
  } catch {
    return null; // corrupt entry is the same as no entry
  }
}

export function clearCityToken() {
  localStorage.removeItem(CITY_TOKEN_KEY);
}

// R10 H1-1: operator-context READS. The server resolves the city from the session and answers 403
// for any other city, so naming the city here is belt-and-braces for a session whose token carries
// no `tid` (an operator who is also a platform admin). Never overrides a `tenant_id` a caller set.
const OPERATOR_READ = /^\/?(operator\/|moderation\/queue|city\/analytics)/;

export function withOperatorTenant(config, tenantId) {
  const method = String(config?.method || "get").toLowerCase();
  const url = String(config?.url || "");
  if (method !== "get" || !tenantId || !OPERATOR_READ.test(url)) return config;
  if (config.params && config.params.tenant_id) return config;
  config.params = { ...(config.params || {}), tenant_id: tenantId };
  return config;
}

api.interceptors.request.use((config) => {
  withOperatorTenant(config, localStorage.getItem(TENANT_ID_KEY));
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const cityToken = getCityToken();
  if (cityToken) config.headers["x-city-token"] = cityToken;
  return config;
});

// The interceptor below runs outside React and can't hold component state itself, so a
// top-level provider (SignInPrompt) registers a callback here on mount. That's the whole
// wiring: no event library, just a module-level ref the interceptor calls into.
let unauthorizedHandler = null;
export function onUnauthorized(handler) {
  unauthorizedHandler = handler;
}

// Registered by AppContext, which owns tenant resolution. Same module-level-ref pattern as
// `onUnauthorized` above, for the same reason: the interceptor runs outside React.
let cityTokenRefresher = null;
export function onCityTokenExpired(handler) {
  cityTokenRefresher = handler;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;

    // A stale visitor token and a genuine "you must sign in" are the SAME 401 with the same
    // message — `visitor_tenant` answers "Choose a city to continue." for a missing, expired,
    // malformed or access-token-bearing header alike. So the only way to tell them apart is to
    // re-resolve the city once and see whether the request then succeeds. Doing that before the
    // sign-in modal fires is what stops a 12-hour-old tab from asking a signed-out visitor to log
    // in just to read a public directory page.
    if (
      status === 401 &&
      config &&
      !config._cityRetried &&
      cityTokenRefresher
    ) {
      config._cityRetried = true;
      try {
        const refreshed = await cityTokenRefresher();
        if (refreshed) {
          config.headers = { ...config.headers, "x-city-token": refreshed };
          return api.request(config);
        }
      } catch {
        // fall through to the normal 401 handling below
      }
    }

    // "Verified phone required" is answered by the server at every citizen write (VerifiedCtx).
    // Ask for the number inline ONCE, then replay the original request so the action continues;
    // browsing never gets here. A dismissed sheet falls through to the ordinary error.
    if (isPhoneNotVerified(error) && config && !config._phoneRetried && phoneGateAvailable()) {
      config._phoneRetried = true;
      if (await requestPhoneVerification()) return api.request(config);
    }

    // Requests that already handle a 401 gracefully (e.g. a silent "am I logged in" probe on
    // boot) opt out with `skipAuthPrompt` so the modal doesn't pop for expected, harmless 401s.
    if (status === 401 && !config?.skipAuthPrompt && unauthorizedHandler) {
      unauthorizedHandler();
    }
    return Promise.reject(error);
  },
);

/** A 422 for a bad phone comes in two shapes, and both get the friendly, translated copy instead of
 * a raw error (`t` is the app's translate fn):
 *  - R11 typed fields (`MobilePhone`/`ListingPhone`/`ContactPhone`): `details.<field> = "invalid_phone"`
 *    plus `fields[]` entries with `code: "validation.invalid_phone"` and `phone_kind`, which picks the
 *    per-kind copy (`PHONE_ERROR_KEYS`);
 *  - R8-1 service-level `canonical_phone`: `details.<..phone..> = "invalid"` (a mobile). */
export function phoneAwareError(error, t, fallback) {
  const details = error?.response?.data?.error?.details;
  if (details && typeof details === "object") {
    const fields = Array.isArray(details.fields) ? details.fields : [];
    const typed = fields.find((f) => f?.code === "validation.invalid_phone");
    if (typed) return t(PHONE_ERROR_KEYS[typed.phone_kind] || PHONE_ERROR_KEYS.mobile);
    const bad = Object.entries(details).some(
      ([k, v]) => /phone|whatsapp/i.test(k) && (v === "invalid" || v === "invalid_phone"),
    );
    if (bad) return t("errors.invalidMobile");
  }
  return apiErrorMessage(error, fallback);
}

export function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  // A 422 carries the failing field(s) at error.details.fields[] as {loc, msg} — no submitted
  // values (the backend strips those as PII). Surface the first one ("amount: not allowed here")
  // instead of the generic message, which is what hid the operator contract bugs. See P3-1.
  const fields = data?.error?.details?.fields;
  if (Array.isArray(fields) && fields.length > 0) {
    const first = fields[0];
    const loc = Array.isArray(first?.loc) ? first.loc : [];
    const name = loc.filter((p) => !["body", "query", "path"].includes(p)).pop();
    const msg = typeof first?.msg === "string" ? first.msg : "";
    if (name && msg) return `${name}: ${msg}`;
    if (msg) return msg;
  }
  // A custom ApnaError (e.g. "Choose a city to report on.") carries its message on the envelope.
  const message = data?.error?.message;
  if (typeof message === "string" && message) return message;
  // Legacy / mirrored shape.
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}
